import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable button component designed primarily to display an icon.
 * It supports different visual variants (e.g., for delete actions).
 * Uses semantic styling and configuration support.
 *
 * @component
 * @param {object} props - The component props.
 * @param {React.ElementType} props.icon - The icon component to render (e.g., FaTrash from react-icons).
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onClick - The function to call when the button is clicked.
 * @param {string} props['aria-label'] - Accessible label for the button, describing its action.
 * @param {'default' | 'danger' | 'move'} [props.variant='default'] - The style variant of the button.
 * @param {boolean} [props.disabled=false] - Whether the button should be disabled.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the button.
 * @param {string} [props.size] - Optional size for the icon (e.g., '0.9em').
 * @returns {React.ReactElement} The rendered IconButton component.
 *
 * @example
 * <IconButton icon={FaTrash} onClick={handleDelete} aria-label="Delete item" variant="danger" />
 * <IconButton icon={FaArrowUp} onClick={moveUp} aria-label="Move item up" variant="move" />
 */
const IconButton = ({ 
    icon: Icon, 
    onClick, 
    'aria-label': ariaLabel, 
    title,
    variant = 'default', 
    disabled = false, 
    className = '', 
    size 
}) => {
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();

    const baseClasses = 'p-1.5 rounded-full inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

    let variantClasses = '';
    switch (variant) {
        case 'danger':
            variantClasses = 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 focus:ring-red-500';
            break;
        case 'move':
            variantClasses = `${semantic.contentCardText} hover:${semantic.contentCardHeading} hover:bg-neutral-100 dark:hover:bg-slate-800 focus:ring-primary-500`;
            break;
        case 'default':
        default:
            variantClasses = `${semantic.contentCardText} hover:${semantic.contentCardHeading} hover:bg-neutral-100 dark:hover:bg-slate-800 focus:ring-primary-500`;
            break;
    }

    const iconProps = size ? { size: size } : {};

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={replace(ariaLabel)}
            title={replace(title || ariaLabel)}
            className={`${baseClasses} ${variantClasses} ${className}`}
        >
            <Icon {...iconProps} />
        </button>
    );
};

IconButton.propTypes = {
    icon: PropTypes.elementType.isRequired,
    onClick: PropTypes.func.isRequired,
    'aria-label': PropTypes.string.isRequired,
    title: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'danger', 'move']),
    disabled: PropTypes.bool,
    className: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default IconButton;

